import { useCallback, useMemo } from 'react';
import ReactGa from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Route } from '@/constants';
import { useNotifications } from '@/features/notifications';
import { TechnologyCollection, TechnologyType } from '@/features/projects/types';
import { useGetCurrentUserQuery } from '@/features/users';

import { useCreateOpportunityMutation } from '../api';
import { CreateOpportunityFormFields } from '../components';

export function useOnCreateOpportunity(setIsOpen: (arg0: boolean) => void) {
  const { t } = useTranslation(['opportunities']);
  const { data: user } = useGetCurrentUserQuery();

  const [createOpportunity, { isLoading: isLoading }] = useCreateOpportunityMutation();

  const { add } = useNotifications();
  const navigate = useNavigate();

  const collections: Record<TechnologyType, TechnologyCollection> = useMemo(
    () => ({
      bess: 'projects_bess_technologies',
      solar: 'projects_solar_technologies',
      wind: 'projects_wind_technologies',
      hydrogen: 'projects_hydrogen_technologies',
      biogas: 'projects_biogas_technologies',
      hidropower: 'projects_hidropower_technologies',
      datacenter: 'projects_datacenter_technologies',
      topco: 'projects_topco_technologies',
    }),
    []
  );

  const hasTechnologies = (projects: CreateOpportunityFormFields['projects']) => {
    return projects.every(
      (p) =>
        p.solar.enabled ||
        p.wind.enabled ||
        p.bess.enabled ||
        p.hydrogen.enabled ||
        p.biogas.enabled ||
        p.hidropower.enabled ||
        p.datacenter.enabled ||
        p.topco.enabled
    );
  };

  return useCallback(
    async (data: CreateOpportunityFormFields) => {
      if (!user) return;
      if (isLoading) return;

      if (!hasTechnologies(data.projects)) {
        add({
          id: new Date().toString(),
          title: t('ERROR'),
          description: t('Each project must have at least one technology selected.'),
        });
        return;
      }

      const mappedProjects = data.projects.map((project) => ({
        project: {
          company: user.company.id,
          title: data.name,
          country: project.country,
          city: project.city,
          technologies: (
            ['solar', 'wind', 'bess', 'hydrogen', 'biogas', 'hidropower', 'datacenter', 'topco'] as TechnologyType[]
          )
            .filter((tech) => project[tech].enabled)
            .map((tech) => ({
              collection: collections[tech],
              item: {},
            })),
        },
      }));

      createOpportunity({
        company: user.company.id,
        name: data.name,
        minimum_percentage: data.minimum_percentage,
        maximum_percentage: data.maximum_percentage,
        options: data.options,
        comments: data.comments,
        projects: mappedProjects,
        status: 'draft',
        about_you: data.about_you,
        advisory_service_information: data.advisory_service_information,
      })
        .unwrap()
        .then((response: any) => {
          add({
            id: new Date().toString(),
            title: t('New Opportunity created'),
            description: t('Your new opportunity has been created'),
          });
          ReactGa.event({
            category: 'form_submitted',
            action: 'Opportunity created',
            label: 'User has created a new opportunity.',
          });
          navigate(`${Route.SellingOpportunity.replace(':opportunityId', response.data.id)}`);
        })
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error creating the opportunity. Please try again later.'),
          });
        })

        .finally(() => setIsOpen(false));
    },
    [user, isLoading, t, add, setIsOpen, createOpportunity, collections, navigate]
  );
}
